import React, { useEffect, useRef, useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import {
  CheckoutOneOffCardForm,
  CheckoutOneOffCardFormHandle,
  OneOffPaymentMethodResult,
} from "./CheckoutOneOffCardForm";
import { CheckoutReplacePaymentMethodDialog } from "./CheckoutReplacePaymentMethodDialog";
import { saveCheckoutCardToBankAccount } from "./checkoutSaveCard";
import { SavedBankAccountForLabel } from "./checkoutPaymentLabels";
import { useAuth } from "../../hooks/useAuth";
import { useMutationUpdateBankAccount } from "../../queries/bankAccount/hooks/useMutationUpdateBankAccount";
import { BANK_ACCOUNTS } from "../../queries/pharmacy/pharmacyTypeDefs";

type CheckoutOneOffCardDialogProps = {
  open: boolean;
  buyerEmail?: string;
  submitting?: boolean;
  /** Existing saved card row only (never ACH). Null when buyer has no saved card yet. */
  savedCardAccount: (SavedBankAccountForLabel & {
    id: number;
    accountId?: string | null;
  }) | null;
  pharmacyId: number;
  userId: number;
  onClose: () => void;
  /** Called after order path is chosen; omit paymentMethodId when card was saved to Bank Account. */
  onConfirm: (paymentMethodId?: string) => Promise<void>;
  onCardSaved?: () => void;
};

export const CheckoutOneOffCardDialog: React.FunctionComponent<CheckoutOneOffCardDialogProps> = ({
  open,
  buyerEmail,
  submitting = false,
  savedCardAccount,
  pharmacyId,
  userId,
  onClose,
  onConfirm,
  onCardSaved,
}) => {
  const stripe = useStripe();
  const { token } = useAuth();
  const oneOffCardRef = useRef<CheckoutOneOffCardFormHandle>(null);
  const confirmInFlightRef = useRef(false);
  const replaceConfirmInFlightRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [pendingPayment, setPendingPayment] = useState<OneOffPaymentMethodResult | null>(null);
  const [savingCard, setSavingCard] = useState(false);

  useEffect(() => {
    if (!open) {
      confirmInFlightRef.current = false;
      replaceConfirmInFlightRef.current = false;
      setConfirming(false);
    }
  }, [open]);

  const [updateBankAccount] = useMutationUpdateBankAccount({
    refetchQueries: [
      {
        query: BANK_ACCOUNTS,
        variables: {
          where: {
            AND: [
              { object: { in: ["payment_method", "card"] } },
              { userId: { equals: userId } },
              { status: { in: ["VERIFIED", "APPROVED"] } },
              { accountId: { not: { equals: null } } },
            ],
          },
        },
      },
    ],
    awaitRefetchQueries: true,
  });

  const cardDialogBusy = submitting || savingCard || confirming;

  const saveCardAndConfirm = async (result: OneOffPaymentMethodResult) => {
    if (!stripe || !token) {
      throw new Error("Card payment is not ready yet. Please wait a moment.");
    }

    await saveCheckoutCardToBankAccount({
      stripe,
      token,
      paymentMethodId: result.paymentMethodId,
      cardholderName: result.cardholderName,
      cardBrand: result.brand,
      cardLast4: result.last4,
      bankAccountId: savedCardAccount?.id,
      pharmacyId,
      userId,
      // Only detach a previous card — never the ACH method.
      oldAccountId: savedCardAccount?.accountId,
      updateBankAccount,
    });

    onCardSaved?.();
    await onConfirm();
  };

  const handleConfirm = async () => {
    if (!oneOffCardRef.current || cardDialogBusy || confirmInFlightRef.current) {
      return;
    }
    confirmInFlightRef.current = true;
    setConfirming(true);
    setError(null);
    let awaitingReplaceConfirm = false;
    try {
      const result = await oneOffCardRef.current.createOneOffPaymentMethod();

      if (oneOffCardRef.current.getSaveCard()) {
        // Only prompt when replacing an existing saved card (ACH is never replaced).
        if (savedCardAccount?.accountId) {
          awaitingReplaceConfirm = true;
          setPendingPayment(result);
          setReplaceDialogOpen(true);
          return;
        }

        setSavingCard(true);
        await saveCardAndConfirm(result);
        return;
      }

      await onConfirm(result.paymentMethodId);
    } catch (err: any) {
      setError(err?.message || "Could not use this card.");
    } finally {
      if (!awaitingReplaceConfirm) {
        confirmInFlightRef.current = false;
        setConfirming(false);
        setSavingCard(false);
      }
    }
  };

  const handleReplaceConfirm = async () => {
    if (
      !pendingPayment ||
      !stripe ||
      !token ||
      savingCard ||
      submitting ||
      replaceConfirmInFlightRef.current
    ) {
      return;
    }
    replaceConfirmInFlightRef.current = true;
    setSavingCard(true);
    setError(null);
    try {
      await saveCardAndConfirm(pendingPayment);
      setReplaceDialogOpen(false);
      setPendingPayment(null);
    } catch (err: any) {
      setError(err?.message || "Could not save card.");
      setReplaceDialogOpen(false);
    } finally {
      replaceConfirmInFlightRef.current = false;
      setSavingCard(false);
      confirmInFlightRef.current = false;
      setConfirming(false);
    }
  };

  const handleReplaceCancel = () => {
    if (savingCard) {
      return;
    }
    setReplaceDialogOpen(false);
    setPendingPayment(null);
    confirmInFlightRef.current = false;
    setConfirming(false);
  };

  const handleClose = () => {
    if (cardDialogBusy) {
      return;
    }
    setError(null);
    setReplaceDialogOpen(false);
    setPendingPayment(null);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
        disableAutoFocus
      >
        <DialogTitle>Enter card details</DialogTitle>
        <DialogContent sx={{ overflow: "visible" }}>
          {open && <CheckoutOneOffCardForm ref={oneOffCardRef} defaultEmail={buyerEmail} />}
          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={cardDialogBusy}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={handleConfirm} disabled={cardDialogBusy}>
            {cardDialogBusy ? "Confirming…" : "Confirm Order"}
            {cardDialogBusy && <CircularProgress sx={{ ml: 1 }} size={14} color="inherit" />}
          </Button>
        </DialogActions>
      </Dialog>

      {pendingPayment && savedCardAccount && (
        <CheckoutReplacePaymentMethodDialog
          open={replaceDialogOpen}
          saving={savingCard}
          currentMethod={savedCardAccount}
          newCardBrand={pendingPayment.brand}
          newCardLast4={pendingPayment.last4}
          onCancel={handleReplaceCancel}
          onConfirm={handleReplaceConfirm}
        />
      )}
    </>
  );
};

export default CheckoutOneOffCardDialog;
